import { useState } from 'react';
import { Plus, Edit, Trash, Power, Thermometer, Sliders, SwitchCamera } from 'lucide-react';

const AutomationScreen = () => {
  const [activeTab, setActiveTab] = useState('devices');
  const [devices, setDevices] = useState([
    { id: 1, name: 'Lâmpada da Sala', type: 'toggle', isOn: true, power: 80 },
    { id: 2, name: 'Ar Condicionado', type: 'temperature', isOn: false, temperature: 22 },
    { id: 3, name: 'Ventilador', type: 'power', isOn: true, power: 40 },
  ]);
  const [scenes, setScenes] = useState([
    { id: 1, name: 'Modo Cinema', devices: [{ id: 1, isOn: false }, { id: 2, isOn: true, temperature: 23 }] },
    { id: 2, name: 'Modo Noturno', devices: [{ id: 1, isOn: false }, { id: 2, isOn: true, temperature: 24 }, { id: 3, isOn: false }] },
  ]);
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [isAddingScene, setIsAddingScene] = useState(false);
  const [isEditingScene, setIsEditingScene] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('toggle');
  const [newSceneName, setNewSceneName] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [currentScene, setCurrentScene] = useState(null);
  const [sceneDeviceSettings, setSceneDeviceSettings] = useState({});

  const deviceTypes = [
    { value: 'toggle', label: 'Liga/Desliga', icon: <SwitchCamera size={18} /> },
    { value: 'power', label: 'Controle de Potência', icon: <Sliders size={18} /> },
    { value: 'temperature', label: 'Controle de Temperatura', icon: <Thermometer size={18} /> }
  ];

  const handleToggleDevice = (id) => {
    setDevices(devices.map(device => 
      device.id === id ? { ...device, isOn: !device.isOn } : device
    ));
  };

  const handlePowerChange = (id, value) => {
    setDevices(devices.map(device => 
      device.id === id ? { ...device, power: value } : device
    ));
  };

  const handleTemperatureChange = (id, value) => {
    setDevices(devices.map(device => 
      device.id === id ? { ...device, temperature: value } : device
    ));
  };

  const handleAddDevice = () => {
    if (newDeviceName.trim()) {
      const newDevice = {
        id: devices.length ? Math.max(...devices.map(d => d.id)) + 1 : 1,
        name: newDeviceName,
        type: newDeviceType,
        isOn: false,
        power: 50,
        temperature: 22
      };
      setDevices([...devices, newDevice]);
      setNewDeviceName('');
      setNewDeviceType('toggle');
      setIsAddingDevice(false);
    }
  };

  const handleDeleteDevice = (id) => {
    setDevices(devices.filter(device => device.id !== id));
    // Remove o dispositivo de todas as cenas
    setScenes(scenes.map(scene => ({
      ...scene,
      devices: scene.devices.filter(device => device.id !== id)
    })));
  };

  const handleAddScene = () => {
    if (newSceneName.trim()) {
      const newScene = {
        id: scenes.length ? Math.max(...scenes.map(s => s.id)) + 1 : 1,
        name: newSceneName,
        devices: []
      };
      setScenes([...scenes, newScene]);
      setNewSceneName('');
      setIsAddingScene(false);
    }
  };

  const handleDeleteScene = (id) => {
    setScenes(scenes.filter(scene => scene.id !== id));
  };

  const handleActivateScene = (sceneId) => {
    const scene = scenes.find(s => s.id === sceneId);
    if (scene) {
      const updatedDevices = [...devices];
      scene.devices.forEach(sceneDevice => {
        const deviceIndex = updatedDevices.findIndex(d => d.id === sceneDevice.id);
        if (deviceIndex !== -1) {
          const device = updatedDevices[deviceIndex];
          const updates = {
            ...device,
            isOn: sceneDevice.isOn
          };
          
          if (device.type === 'power' && sceneDevice.power !== undefined) {
            updates.power = sceneDevice.power;
          } else if (device.type === 'temperature' && sceneDevice.temperature !== undefined) {
            updates.temperature = sceneDevice.temperature;
          }
          
          updatedDevices[deviceIndex] = updates;
        }
      });
      setDevices(updatedDevices);
    }
  };

  const handleEditItem = (item, type) => {
    setEditingItem({ ...item, type });
  };

  const handleUpdateItem = () => {
    if (editingItem) {
      if (editingItem.type === 'device') {
        setDevices(devices.map(device => 
          device.id === editingItem.id ? { ...editingItem } : device
        ));
      } else {
        setScenes(scenes.map(scene => 
          scene.id === editingItem.id ? { ...editingItem } : scene
        ));
      }
      setEditingItem(null);
    }
  };

  const handleEditScene = (scene) => {
    const initialSettings = {};
    scene.devices.forEach(sceneDevice => {
      initialSettings[sceneDevice.id] = { ...sceneDevice };
    });
    
    setCurrentScene(scene);
    setSceneDeviceSettings(initialSettings);
    setIsEditingScene(true);
  };

  const handleToggleDeviceInScene = (deviceId) => {
    setSceneDeviceSettings(prev => {
      const newSettings = { ...prev };
      if (newSettings[deviceId]) {
        // Se já existe na cena, atualiza o valor isOn
        newSettings[deviceId] = { 
          ...newSettings[deviceId], 
          isOn: !newSettings[deviceId].isOn 
        };
      } else {
        // Se não existe na cena, adiciona com os valores padrão
        const device = devices.find(d => d.id === deviceId);
        newSettings[deviceId] = { 
          id: deviceId, 
          isOn: true,
          power: device.type === 'power' ? device.power : undefined,
          temperature: device.type === 'temperature' ? device.temperature : undefined
        };
      }
      return newSettings;
    });
  };

  const handleUpdateDeviceValueInScene = (deviceId, type, value) => {
    setSceneDeviceSettings(prev => {
      const newSettings = { ...prev };
      if (newSettings[deviceId]) {
        newSettings[deviceId] = { 
          ...newSettings[deviceId], 
          [type]: value
        };
      }
      return newSettings;
    });
  };

  const saveSceneSettings = () => {
    if (currentScene) {
      const updatedScene = {
        ...currentScene,
        devices: Object.values(sceneDeviceSettings).filter(device => device.isOn)
      };
      
      setScenes(scenes.map(scene => 
        scene.id === currentScene.id ? updatedScene : scene
      ));
      
      setIsEditingScene(false);
      setCurrentScene(null);
      setSceneDeviceSettings({});
    }
  };

  const isDeviceInScene = (deviceId, sceneDevices) => {
    return sceneDevices.some(device => device.id === deviceId);
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'toggle':
        return <SwitchCamera size={18} />;
      case 'power':
        return <Sliders size={18} />;
      case 'temperature':
        return <Thermometer size={18} />;
      default:
        return <SwitchCamera size={18} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Navigation Tabs */}
      <div className="flex bg-white shadow">
        <button 
          className={`flex-1 py-3 font-medium ${activeTab === 'devices' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('devices')}
        >
          Dispositivos
        </button>
        <button 
          className={`flex-1 py-3 font-medium ${activeTab === 'scenes' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('scenes')}
        >
          Cenas
        </button>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4">
        {activeTab === 'devices' ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Meus Dispositivos</h2>
              <button 
                className="flex items-center gap-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
                onClick={() => setIsAddingDevice(true)}
              >
                <Plus size={16} /> Adicionar
              </button>
            </div>

            {/* Device List */}
            <div className="space-y-4">
              {devices.map(device => (
                <div key={device.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">
                        {getDeviceIcon(device.type)}
                      </span>
                      <h3 className="font-medium text-gray-800">{device.name}</h3>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        className="text-gray-500 hover:text-green-600"
                        onClick={() => handleEditItem(device, 'device')}
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="text-gray-500 hover:text-red-600"
                        onClick={() => handleDeleteDevice(device.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button 
                        className={`p-2 rounded-full ${device.isOn ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                        onClick={() => handleToggleDevice(device.id)}
                      >
                        <Power size={18} />
                      </button>
                      <span className="text-sm text-gray-600">{device.isOn ? 'Ligado' : 'Desligado'}</span>
                    </div>
                    {device.isOn && (
                      device.type === 'power' ? (
                        <div className="flex items-center gap-2 flex-1 ml-4">
                          <span className="text-sm text-gray-600">Potência:</span>
                          <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={device.power} 
                            onChange={(e) => handlePowerChange(device.id, parseInt(e.target.value))}
                            className="flex-1 accent-green-600" 
                          />
                          <span className="text-sm font-medium text-gray-700">{device.power}%</span>
                        </div>
                      ) : device.type === 'temperature' ? (
                        <div className="flex items-center gap-2 flex-1 ml-4">
                          <span className="text-sm text-gray-600">Temperatura:</span>
                          <input 
                            type="range" 
                            min="16" 
                            max="30" 
                            value={device.temperature} 
                            onChange={(e) => handleTemperatureChange(device.id, parseInt(e.target.value))}
                            className="flex-1 accent-green-600" 
                          />
                          <span className="text-sm font-medium text-gray-700">{device.temperature}°C</span>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Device Form */}
            {isAddingDevice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-medium mb-4">Adicionar Dispositivo</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do dispositivo</label>
                    <input
                      type="text"
                      placeholder="Ex: Lâmpada da Sala"
                      className="w-full border border-gray-300 rounded-md p-2"
                      value={newDeviceName}
                      onChange={(e) => setNewDeviceName(e.target.value)}
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de controle</label>
                    <div className="grid grid-cols-1 gap-2">
                      {deviceTypes.map(type => (
                        <button
                          key={type.value}
                          className={`flex items-center gap-2 p-2 border rounded-md ${
                            newDeviceType === type.value 
                              ? 'border-green-600 bg-green-50 text-green-700' 
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setNewDeviceType(type.value)}
                        >
                          <span className={`p-1 rounded-full ${newDeviceType === type.value ? 'bg-green-100' : 'bg-gray-100'}`}>
                            {type.icon}
                          </span>
                          <span>{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button 
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                      onClick={() => setIsAddingDevice(false)}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      onClick={handleAddDevice}
                    >
                      Adicionar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Minhas Cenas</h2>
              <button 
                className="flex items-center gap-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
                onClick={() => setIsAddingScene(true)}
              >
                <Plus size={16} /> Adicionar
              </button>
            </div>

            {/* Scene List */}
            <div className="space-y-4">
              {scenes.map(scene => (
                <div key={scene.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-gray-800">{scene.name}</h3>
                    <div className="flex gap-2">
                      <button 
                        className="text-gray-500 hover:text-green-600"
                        onClick={() => handleEditScene(scene)}
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="text-gray-500 hover:text-red-600"
                        onClick={() => handleDeleteScene(scene.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 mb-4">
                    <p className="text-sm text-gray-600">{scene.devices.length} dispositivos configurados</p>
                    {scene.devices.length > 0 && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-md">
                        <p className="text-sm font-medium text-gray-700 mb-1">Dispositivos na cena:</p>
                        <div className="space-y-1">
                          {scene.devices.map(sceneDevice => {
                            const device = devices.find(d => d.id === sceneDevice.id);
                            return device ? (
                              <div key={device.id} className="flex items-center text-sm text-gray-600">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                                {device.name} - {sceneDevice.isOn ? 'Ligado' : 'Desligado'}
                                {device.type === 'power' && sceneDevice.power !== undefined && 
                                  ` (${sceneDevice.power}%)`}
                                {device.type === 'temperature' && sceneDevice.temperature !== undefined && 
                                  ` (${sceneDevice.temperature}°C)`}
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    className="w-full py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 font-medium"
                    onClick={() => handleActivateScene(scene.id)}
                  >
                    Ativar Cena
                  </button>
                </div>
              ))}
            </div>

            {/* Add Scene Form */}
            {isAddingScene && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-medium mb-4">Adicionar Cena</h3>
                  <input
                    type="text"
                    placeholder="Nome da cena"
                    className="w-full border border-gray-300 rounded-md p-2 mb-4"
                    value={newSceneName}
                    onChange={(e) => setNewSceneName(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <button 
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                      onClick={() => setIsAddingScene(false)}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      onClick={handleAddScene}
                    >
                      Adicionar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Scene Form */}
            {isEditingScene && currentScene && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-auto">
                  <h3 className="text-lg font-medium mb-2">Editar Cena: {currentScene.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">Selecione os dispositivos que farão parte desta cena</p>
                  
                  <div className="space-y-3 mb-6">
                    {devices.map(device => {
                      const isSelected = sceneDeviceSettings[device.id] && sceneDeviceSettings[device.id].isOn;
                      return (
                        <div key={device.id} className="border border-gray-200 rounded-md p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-green-600">
                                {getDeviceIcon(device.type)}
                              </span>
                              <span className="font-medium">{device.name}</span>
                            </div>
                            <label className="inline-flex items-center cursor-pointer">
                              <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={isSelected}
                                onChange={() => handleToggleDeviceInScene(device.id)}
                              />
                              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                            </label>
                          </div>
                          {isSelected && (
                            <div className="mt-2 pl-2 border-l-2 border-green-200">
                              <div className="flex items-center mb-2">
                                <span className="text-sm text-gray-600 w-24">Estado:</span>
                                <div className="flex items-center gap-4">
                                  <label className="inline-flex items-center">
                                    <input 
                                      type="radio" 
                                      className="accent-green-600"
                                      checked={sceneDeviceSettings[device.id].isOn}
                                      onChange={() => handleUpdateDeviceValueInScene(device.id, 'isOn', true)}
                                    />
                                    <span className="ml-1 text-sm">Ligado</span>
                                  </label>
                                  <label className="inline-flex items-center">
                                    <input 
                                      type="radio" 
                                      className="accent-green-600"
                                      checked={!sceneDeviceSettings[device.id].isOn}
                                      onChange={() => handleUpdateDeviceValueInScene(device.id, 'isOn', false)}
                                    />
                                    <span className="ml-1 text-sm">Desligado</span>
                                  </label>
                                </div>
                              </div>
                              {device.type === 'power' && sceneDeviceSettings[device.id].isOn && (
                                <div className="flex items-center mb-2">
                                  <span className="text-sm text-gray-600 w-24">Potência:</span>
                                  <div className="flex items-center gap-2 flex-1">
                                    <input 
                                      type="range" 
                                      min="0" 
                                      max="100" 
                                      value={sceneDeviceSettings[device.id].power || 50} 
                                      onChange={(e) => handleUpdateDeviceValueInScene(device.id, 'power', parseInt(e.target.value))}
                                      className="flex-1 accent-green-600" 
                                    />
                                    <span className="text-sm font-medium text-gray-700 w-10">
                                      {sceneDeviceSettings[device.id].power || 50}%
                                    </span>
                                  </div>
                                </div>
                              )}
                              {device.type === 'temperature' && sceneDeviceSettings[device.id].isOn && (
                                <div className="flex items-center mb-2">
                                  <span className="text-sm text-gray-600 w-24">Temperatura:</span>
                                  <div className="flex items-center gap-2 flex-1">
                                    <input 
                                      type="range" 
                                      min="16" 
                                      max="30" 
                                      value={sceneDeviceSettings[device.id].temperature || 22} 
                                      onChange={(e) => handleUpdateDeviceValueInScene(device.id, 'temperature', parseInt(e.target.value))}
                                      className="flex-1 accent-green-600" 
                                    />
                                    <span className="text-sm font-medium text-gray-700 w-10">
                                      {sceneDeviceSettings[device.id].temperature || 22}°C
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button 
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                      onClick={() => {
                        setIsEditingScene(false);
                        setCurrentScene(null);
                      }}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      onClick={saveSceneSettings}
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Item Modal */}
        {editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium mb-4">
                Editar {editingItem.type === 'device' ? 'Dispositivo' : 'Cena'}
              </h3>
              <input
                type="text"
                placeholder={`Nome do ${editingItem.type === 'device' ? 'dispositivo' : 'cena'}`}
                className="w-full border border-gray-300 rounded-md p-2 mb-4"
                value={editingItem.name}
                onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
              />
              {editingItem.type === 'device' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de controle</label>
                  <select 
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={editingItem.type}
                    onChange={(e) => setEditingItem({...editingItem, type: e.target.value})}
                  >
                    <option value="toggle">Liga/Desliga</option>
                    <option value="power">Controle de Potência</option>
                    <option value="temperature">Controle de Temperatura</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button 
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                  onClick={() => setEditingItem(null)}
                >
                  Cancelar
                </button>
                <button 
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  onClick={handleUpdateItem}
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AutomationScreen
